import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/reducers";
import { backSteep } from "../../store/actions/fichaMedicaActions";
import Api from "../../utils/api";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../Button";
import IFichaCargaMedica from "../../interfaces/IFichaMedicaCarga";
import OBJtoXML from "../../utils/objToXML";
import formToObj from "../../utils/formToObj";
import Modal from "../Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { isLake } from "../../utils/brand";

export default function HistoriaClinica() {
  const form = useRef(null);
  const fichaMedica = useSelector((state: RootState) => state.fichaMedica);

  const [apiMessage, setApiMessage] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // Controlado para mostrar/ocultar los inputs de embarazo según la respuesta.
  const [embarazo, setEmbarazo] = useState("");

  const { id = "-1", nombre } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    setEmbarazo(fichaMedica.campos.M_EMBARAZO || "");
  }, [fichaMedica]);

  function handleClickVolver() {
    dispatch(backSteep());
  }

  function handleClickValidar() {
    setApiMessage("");
    setIsFetching(true);

    if (!form.current) {
      return;
    }
    const body = formToObj(form.current);

    let obj: IFichaCargaMedica = fichaMedica;
    obj = {
      ...obj,
      campos: {
        ...obj.campos,
        ...body,
      },
      steep: 5,
    };
    Api("consumir")
      .post({
        servicio: "fichaMedicaSet",
        param: {
          pc_request: OBJtoXML({ FICHA_MEDICA: obj.campos }),
          pco_pasajero: id,
          pn_pagina: 5,
        },
      })
      .then((res) => {
        setIsFetching(false);
        if (res.mensaje.tipo === "E") {
          setApiMessage(res.mensaje.descripcion);
          return;
        }
        setShowModal(true);
      });
  }

  const handleCloseModal = () => {
    navigate(`/pasajero/${id}/${nombre}/ficha-medica`);
  };

  // const localDateToDate = (dateString: string) => {
  //   if (!dateString) return "";
  //   const [day, month, year] = dateString.split("/");
  //   const date = new Date(Number(year), Number(month) - 1, Number(day));
  //   return date.toISOString().split("T")[0]; // Convert to YYYY-MM-DD format
  // };

  // La sección Embarazo es solo para pasajeras femeninas. Se chequea por código
  // ("F") o por la etiqueta del sexo seleccionado ("FEMENINO"), para no depender
  // de un único formato del catálogo.
  const sexoDato =
    fichaMedica.sexos.find((s) => s.codigo === fichaMedica.campos.C_SEXO)?.dato ||
    "";
  const esFemenino =
    fichaMedica.campos.C_SEXO === "F" ||
    sexoDato.toLocaleUpperCase().startsWith("FEMEN");

  return (
    <form ref={form} className="container mb-5">
      <h3 className="color-platform text-center">Historia clínica</h3>
      <div className="row">
        <div className="col-lg-6 col-12 my-2">
          <span>Grupo y Factor Sanguíneo:</span>
          <select
            name="D_GRUPO_SANGUINEO"
            className="form-select"
            defaultValue={fichaMedica.campos.D_GRUPO_SANGUINEO}
          >
            <option value=""></option>
            <option value="A+">A+</option>
            <option value="B+">B+</option>
            <option value="AB+">AB+</option>
            <option value="AB-">AB-</option>
            <option value="A-">A-</option>
            <option value="B-">B-</option>
            <option value="0-">0-</option>
            <option value="0+">0+</option>
          </select>
        </div>
        <div className="col-12"></div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Se encuentra actualmente bajo tratamiento médico?:</span>
          <select
            className="form-select"
            name="M_TRATAMIENTO_MEDICO"
            defaultValue={fichaMedica.campos.M_TRATAMIENTO_MEDICO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Por qué enfermedad?:</span>
          <input
            defaultValue={fichaMedica.campos.D_ENFERMEDAD}
            name="D_ENFERMEDAD"
            className="form-control"
            type="text"
            maxLength={100}
          />
        </div>

        <div className="col-lg-6 col-12 my-2">
          <span>¿Está tomando algún medicamento?:</span>
          <select
            className="form-select"
            name="M_MEDICAMENTO"
            defaultValue={fichaMedica.campos.M_MEDICAMENTO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Cuál?:</span>
          <input
            defaultValue={fichaMedica.campos.D_MEDICAMENTO}
            maxLength={100}
            name="D_MEDICAMENTO"
            className="form-control"
            type="text"
          />
        </div>

        <div className="col-12 my-2">
          <span>¿Cuál es la dosis y la frecuencia diaria?:</span>
          <input
            maxLength={100}
            name="D_DOSIS_FRECUENCIA"
            className="form-control"
            type="text"
            defaultValue={fichaMedica.campos.D_DOSIS_FRECUENCIA}
          />
        </div>

        <div className="col-lg-6 col-12 my-2">
          <span>
            ¿Tuvo algún accidente o internación en los últimos 12 meses?:
          </span>
          <select
            className="form-select"
            name="M_INTERNACION"
            defaultValue={fichaMedica.campos.M_INTERNACION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Por qué motivo? (Diagnóstico):</span>
          <input
            defaultValue={fichaMedica.campos.D_INTERNACION}
            maxLength={100}
            name="D_INTERNACION"
            className="form-control"
            type="text"
          />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Fué sometido a alguna cirugía?:</span>
          <select
            className="form-select"
            name="M_CIRUGIA"
            defaultValue={fichaMedica.campos.M_CIRUGIA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿De qué tipo?:</span>
          <input
            maxLength={100}
            name="D_CIRUGIA"
            className="form-control"
            type="text"
            defaultValue={fichaMedica.campos.D_CIRUGIA}
          />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>
            ¿Tuvo alguna internación en los últimos 12 meses?:
          </span>
          <select
            className="form-select"
            name="INTERNACION_12MESES"
            defaultValue={fichaMedica.campos.INTERNACION_12MESES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Motivos?:</span>
          <input
            maxLength={100}
            name="INTERNACION_12MESES_MOTIVOS"
            className="form-control"
            type="text"
            defaultValue={fichaMedica.campos.INTERNACION_12MESES_MOTIVOS}
          />
        </div>

        <div className="col-lg-6 col-12 my-2">
          <span>¿Es alérgico?:</span>
          <select
            className="form-select"
            name="M_ALERGICO"
            defaultValue={fichaMedica.campos.M_ALERGICO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿A qué cosas?:</span>
          <input
            defaultValue={fichaMedica.campos.D_ALERGICO}
            maxLength={100}
            name="D_ALERGICO"
            className="form-control"
            type="text"
          />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Es alérgico a algún tipo de medicación?:</span>
          <select
            className="form-select"
            name="M_ALERGICO_MEDICACION"
            defaultValue={fichaMedica.campos.M_ALERGICO_MEDICACION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Cuál?:</span>
          <input
            defaultValue={fichaMedica.campos.D_ALERGICO_MEDICACION}
            maxLength={100}
            name="D_ALERGICO_MEDICACION"
            className="form-control"
            type="text"
          />
        </div>
      </div>
      <hr />
      <h3 className="color-platform text-center">Vacunación</h3>
      <div className="row">
        <div className="col-lg-6 col-12 my-2">
          <span>¿Calendario de vacunación al día?:</span>
          <select
            className="form-select"
            name="CALENDARIO_VACUNACION"
            defaultValue={fichaMedica.campos.CALENDARIO_VACUNACION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Vacuna antitetánica:</span>
          <select
            className="form-select"
            name="VACUNA_ANTITETANICA"
            defaultValue={fichaMedica.campos.VACUNA_ANTITETANICA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Vacuna triple viral:</span>
          <select
            className="form-select"
            name="VACUNA_TRIPLEVIRAL"
            defaultValue={fichaMedica.campos.VACUNA_TRIPLEVIRAL}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Vacuna dengue:</span>
          <select
            className="form-select"
            name="VACUNA_DENGUE"
            defaultValue={fichaMedica.campos.VACUNA_DENGUE}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Vacuna COVID:</span>
          <select
            className="form-select"
            name="M_VACUNA_H1N1"
            defaultValue={fichaMedica.campos.M_VACUNA_H1N1}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
      </div>
      {!isLake && esFemenino && (
        <>
          <hr />
          <h3 className="color-platform text-center">Embarazo</h3>
          <div className="row">
            <div className="col-lg-6 col-12 my-2">
              <span>¿Embarazada?</span>
              <select
                className="form-select"
                name="M_EMBARAZO"
                value={embarazo}
                onChange={(e) => setEmbarazo(e.target.value)}
              >
                <option value=""></option>
                <option value="S">Si</option>
                <option value="N">No</option>
              </select>
            </div>
            {embarazo === "S" && (
              <>
                <div className="col-lg-6 col-12 my-2">
                  <span>Mes de gestación:</span>
                  <select
                    className="form-select"
                    name="N_EMBARAZO_MESES_GESTACION"
                    defaultValue={fichaMedica.campos.N_EMBARAZO_MESES_GESTACION}
                  >
                    <option value=""></option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 my-2">
                  <span>Recomendación puntual del médico tratante:</span>
                  <textarea
                    className="form-control"
                    name="D_EMBARAZO_REC_MEDICO"
                    maxLength={1000}
                    rows={4}
                    defaultValue={fichaMedica.campos.D_EMBARAZO_REC_MEDICO}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
      <hr />
      <h3 className="color-platform text-center">
        ¿Padece o padeció alguna de las siguientes enfermedades?
      </h3>
      <div className="row">
        <div className="col-lg-6 col-12 my-2">
          <span>Escoliosis o enfermedades de columna:</span>
          <select
            className="form-select"
            name="M_ENF_COLUMNA"
            defaultValue={fichaMedica.campos.M_ENF_COLUMNA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Asma:</span>
          <select
            className="form-select"
            name="M_ASMA"
            defaultValue={fichaMedica.campos.M_ASMA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Enfermedades de los huesos o articulaciones:</span>
          <select
            className="form-select"
            name="M_ENF_HUESOS_ARTICULACIONES"
            defaultValue={fichaMedica.campos.M_ENF_HUESOS_ARTICULACIONES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Anemia:</span>
          <select
            className="form-select"
            name="M_ANEMIA"
            defaultValue={fichaMedica.campos.M_ANEMIA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Enfermedades de los intestinos:</span>
          <select
            className="form-select"
            name="M_ENF_INTESTINOS"
            defaultValue={fichaMedica.campos.M_ENF_INTESTINOS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Anorexia:</span>
          <select
            className="form-select"
            name="M_ANOREXIA"
            defaultValue={fichaMedica.campos.M_ANOREXIA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Enfermedades renales crónicas:</span>
          <select
            className="form-select"
            name="M_ENF_RENALES"
            defaultValue={fichaMedica.campos.M_ENF_RENALES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Bulimia:</span>
          <select
            className="form-select"
            name="M_BULIMIA"
            defaultValue={fichaMedica.campos.M_BULIMIA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Enfermedades psiquiátricas:</span>
          <select
            className="form-select"
            name="M_ENF_PSIQUIATRICAS"
            defaultValue={fichaMedica.campos.M_ENF_PSIQUIATRICAS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Fracturas:</span>
          <select
            className="form-select"
            name="M_FRACTURAS"
            defaultValue={fichaMedica.campos.M_FRACTURAS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Enfermedades cardiacas:</span>
          <select
            className="form-select"
            name="M_ENF_CARDIACAS"
            defaultValue={fichaMedica.campos.M_ENF_CARDIACAS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Diabetes:</span>
          <select
            className="form-select"
            name="M_DIABETES"
            defaultValue={fichaMedica.campos.M_DIABETES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Epilepsia o convulsiones:</span>
          <select
            className="form-select"
            name="M_EPILEPSIA_CONVULSIONES"
            defaultValue={fichaMedica.campos.M_EPILEPSIA_CONVULSIONES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Trastornos de alimentación:</span>
          <select
            className="form-select"
            name="M_PRO_ALIMENTACION"
            defaultValue={fichaMedica.campos.M_PRO_ALIMENTACION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Fiebre reumática:</span>
          <select
            className="form-select"
            name="M_FIEBRE_REUMATICA"
            defaultValue={fichaMedica.campos.M_FIEBRE_REUMATICA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Gripe:</span>
          <select
            className="form-select"
            name="M_GRIPE"
            defaultValue={fichaMedica.campos.M_GRIPE}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Gastritis o úlcera:</span>
          <select
            className="form-select"
            name="M_GASTITIS_ULCERA"
            defaultValue={fichaMedica.campos.M_GASTITIS_ULCERA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Cólicos renales:</span>
          <select
            className="form-select"
            name="M_COLICOS_RENALES"
            defaultValue={fichaMedica.campos.M_COLICOS_RENALES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Hipertensión arterial:</span>
          <select
            className="form-select"
            name="M_HIPERTENSION"
            defaultValue={fichaMedica.campos.M_HIPERTENSION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Hepatitis:</span>
          <select
            className="form-select"
            name="M_HEPATITIS"
            defaultValue={fichaMedica.campos.M_HEPATITIS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Infecciones urinarias:</span>
          <select
            className="form-select"
            name="M_INFECCIONES_URINARIAS"
            defaultValue={fichaMedica.campos.M_INFECCIONES_URINARIAS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Problemas ginecológicos:</span>
          <select
            className="form-select"
            name="M_PROBLEMAS_GINECOLOGICOS"
            defaultValue={fichaMedica.campos.M_PROBLEMAS_GINECOLOGICOS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Enfermedades de los pulmones:</span>
          <select
            className="form-select"
            name="M_ENF_PULMONES"
            defaultValue={fichaMedica.campos.M_ENF_PULMONES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Cuales?:</span>
          <input
            maxLength={100}
            defaultValue={fichaMedica.campos.D_ENF_PULMONES}
            name="D_ENF_PULMONES"
            className="form-control"
            type="text"
          />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Celiaquía:</span>
          <select
            className="form-select"
            name="CELIAQUIA"
            defaultValue={fichaMedica.campos.CELIAQUIA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Tiroides:</span>
          <select
            className="form-select"
            name="TIROIDES"
            defaultValue={fichaMedica.campos.TIROIDES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Hemofilia:</span>
          <select
            className="form-select"
            name="HEMOFILIA"
            defaultValue={fichaMedica.campos.HEMOFILIA}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>HIV:</span>
          <select
            className="form-select"
            name="HIV"
            defaultValue={fichaMedica.campos.HIV}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Sífilis:</span>
          <select
            className="form-select"
            name="SIFILIS"
            defaultValue={fichaMedica.campos.SIFILIS}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Herpes:</span>
          <select
            className="form-select"
            name="HERPES"
            defaultValue={fichaMedica.campos.HERPES}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-12">
          <hr />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">
            ¿Alguna actividad física desaconsejada por el médico?:
          </label>
          <select
            name="M_AF_DESACONSEJADA"
            className="form-control"
            defaultValue={fichaMedica.campos.M_AF_DESACONSEJADA}
          >
            <option></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">¿Cuál? (Adj. certificado):</label>
          <input
            name="D_AF_DESACONSEJADA"
            defaultValue={fichaMedica.campos.D_AF_DESACONSEJADA}
            type="text"
            maxLength={100}
            className="form-control"
          />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">
            ¿Algún alimento desaconsejado por el médico?:
          </label>
          <select
            name="M_ALIMENTO_DESACONSEJADO"
            className="form-control"
            defaultValue={fichaMedica.campos.M_ALIMENTO_DESACONSEJADO}
          >
            <option></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">¿Cuál? (Adj. certificado):</label>
          <input
            defaultValue={fichaMedica.campos.D_ALIMENTO_DESACONSEJADO}
            name="D_ALIMENTO_DESACONSEJADO"
            type="text"
            maxLength={100}
            className="form-control"
          />
        </div>

        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">
            ¿Viajó al exterior en los últimos 60 días?:
          </label>
          <select
            name="M_VIAJO_EXTERIOR"
            className="form-control"
            defaultValue={fichaMedica.campos.M_VIAJO_EXTERIOR}
          >
            <option></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">¿A dónde?:</label>
          <input
            defaultValue={fichaMedica.campos.D_VIAJO_EXTERIROR}
            name="D_VIAJO_EXTERIROR"
            type="text"
            maxLength={100}
            className="form-control"
          />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">
            ¿Padeció alguna enfermedad?:
          </label>
          <select
            name="M_PADECIO_ENF"
            className="form-control"
            defaultValue={fichaMedica.campos.M_PADECIO_ENF}
          >
            <option></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">¿Cuál?:</label>
          <input
            name="D_PADECIO_ENF"
            type="text"
            maxLength={100}
            className="form-control"
            defaultValue={fichaMedica.campos.D_PADECIO_ENF}
          ></input>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">
            ¿Toma medicamentos en otras situaciones? (fiebre, dolores, etc):
          </label>
          <select
            name="M_MEDICAMENTOS"
            className="form-control"
            defaultValue={fichaMedica.campos.M_MEDICAMENTOS}
          >
            <option></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">¿Cuales?:</label>
          <input
            defaultValue={fichaMedica.campos.D_MEDICAMENTOS}
            name="D_MEDICAMENTOS"
            type="text"
            maxLength={100}
            className="form-control"
          ></input>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">
            ¿Hay algún otro medicamento que no deba tomar?:
          </label>
          <select
            defaultValue={fichaMedica.campos.M_MED_NO_DEBE_TOMAR}
            name="M_MED_NO_DEBE_TOMAR"
            className="form-control"
          >
            <option></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">¿Cuál?:</label>
          <input
            defaultValue={fichaMedica.campos.D_MED_NO_DEBE_TOMAR}
            name="D_MED_NO_DEBE_TOMAR"
            type="text"
            maxLength={100}
            className="form-control"
          />
        </div>
        <div className="col-12 my-2">
          <label className="font-weight-bold">
            El siguiente espacio es para información que se considere necesaria
            para el conocimiento de los profesionales que en caso de
            necesitarlo, atenderán al pasajero:
          </label>
          <textarea
            defaultValue={fichaMedica.campos.D_DATOS_AMPLIADOS}
            className="form-control"
            name="D_DATOS_AMPLIADOS"
            rows={5}
            maxLength={1000}
          ></textarea>
        </div>
        <div className="col-12">
          <hr />
        </div>
        <div className="col-lg-6 col-12 my-2">
          <label className="font-weight-bold">Dieta:</label>
          <select
            name="C_DIETA"
            className="form-control"
            defaultValue={fichaMedica.campos.C_DIETA}
          >
            {fichaMedica.dietas.map((dieta) => (
              <option key={dieta.codigo} value={dieta.codigo}>
                {dieta.dato}
              </option>
            ))}
          </select>
        </div>
      </div>
      {!isLake && (
      <>
      <hr />
      <h3 className="color-platform text-center">
        Tratamientos psicológicos / psiquiátricos
      </h3>
      <p className="text-center">
        <small>
          ¡IMPORTANTE! Informe médico del psiquiatra / psicólogo en caso de tener
          alguna de las siguientes patologías.
        </small>
      </p>
      <div className="row">
        <h5 className="col-12 color-platform mt-2">Ataques de pánico</h5>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Sabés qué es un ataque de pánico?:</span>
          <select
            className="form-select"
            name="M_PANICO_CONOCE"
            defaultValue={fichaMedica.campos.M_PANICO_CONOCE}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Tuviste o sufriste alguna vez?:</span>
          <select
            className="form-select"
            name="M_PANICO_SUFRIO"
            defaultValue={fichaMedica.campos.M_PANICO_SUFRIO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Esos ataques de pánico son:</span>
          <select
            className="form-select"
            name="T_PANICO_FRECUENCIA"
            defaultValue={fichaMedica.campos.T_PANICO_FRECUENCIA}
          >
            <option value=""></option>
            <option value="ESPORADICOS">Esporádicos</option>
            <option value="SEGUIDOS">Seguidos</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Estás bajo tratamiento?:</span>
          <select
            className="form-select"
            name="M_PANICO_TRATAMIENTO"
            defaultValue={fichaMedica.campos.M_PANICO_TRATAMIENTO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Tomaste alguna medicación para tratar eso?:</span>
          <select
            className="form-select"
            name="M_PANICO_MEDICACION"
            defaultValue={fichaMedica.campos.M_PANICO_MEDICACION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Cuál?:</span>
          <input
            maxLength={100}
            name="D_PANICO_MEDICACION"
            className="form-control"
            type="text"
            defaultValue={fichaMedica.campos.D_PANICO_MEDICACION}
          />
        </div>

        <h5 className="col-12 color-platform mt-3">Depresión</h5>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Conocés o sabés qué es la depresión?:</span>
          <select
            className="form-select"
            name="M_DEPRESION_CONOCE"
            defaultValue={fichaMedica.campos.M_DEPRESION_CONOCE}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Tuviste o sufriste dicha patología?:</span>
          <select
            className="form-select"
            name="M_DEPRESION_SUFRIO"
            defaultValue={fichaMedica.campos.M_DEPRESION_SUFRIO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Estás bajo tratamiento?:</span>
          <select
            className="form-select"
            name="M_DEPRESION_TRATAMIENTO"
            defaultValue={fichaMedica.campos.M_DEPRESION_TRATAMIENTO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Tomás alguna medicación para tratarlo?:</span>
          <select
            className="form-select"
            name="M_DEPRESION_MEDICACION"
            defaultValue={fichaMedica.campos.M_DEPRESION_MEDICACION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Cuál?:</span>
          <input
            maxLength={100}
            name="D_DEPRESION_MEDICACION"
            className="form-control"
            type="text"
            defaultValue={fichaMedica.campos.D_DEPRESION_MEDICACION}
          />
        </div>

        <h5 className="col-12 color-platform mt-3">Ansiedad</h5>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Sabés qué es un ataque de ansiedad?:</span>
          <select
            className="form-select"
            name="M_ANSIEDAD_CONOCE"
            defaultValue={fichaMedica.campos.M_ANSIEDAD_CONOCE}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Tuviste o sufriste alguna vez?:</span>
          <select
            className="form-select"
            name="M_ANSIEDAD_SUFRIO"
            defaultValue={fichaMedica.campos.M_ANSIEDAD_SUFRIO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>Esos ataques de ansiedad son:</span>
          <select
            className="form-select"
            name="T_ANSIEDAD_FRECUENCIA"
            defaultValue={fichaMedica.campos.T_ANSIEDAD_FRECUENCIA}
          >
            <option value=""></option>
            <option value="ESPORADICOS">Esporádicos</option>
            <option value="SEGUIDOS">Seguidos</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Estás bajo tratamiento?:</span>
          <select
            className="form-select"
            name="M_ANSIEDAD_TRATAMIENTO"
            defaultValue={fichaMedica.campos.M_ANSIEDAD_TRATAMIENTO}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Tomaste alguna medicación para tratar eso?:</span>
          <select
            className="form-select"
            name="M_ANSIEDAD_MEDICACION"
            defaultValue={fichaMedica.campos.M_ANSIEDAD_MEDICACION}
          >
            <option value=""></option>
            <option value="S">Si</option>
            <option value="N">No</option>
          </select>
        </div>
        <div className="col-lg-6 col-12 my-2">
          <span>¿Cuál?:</span>
          <input
            maxLength={100}
            name="D_ANSIEDAD_MEDICACION"
            className="form-control"
            type="text"
            defaultValue={fichaMedica.campos.D_ANSIEDAD_MEDICACION}
          />
        </div>
      </div>
      </>
      )}
      <hr />
      <div className="text-center mt-4">
        <Button title="Volver" secondary onPress={handleClickVolver} />
        <Button
          title="Guardar Ficha Medica"
          onLoad={isFetching}
          onPress={handleClickValidar}
        />
      </div>
      {apiMessage ? (
        <div
          className="text-danger"
          dangerouslySetInnerHTML={{ __html: apiMessage }}
        ></div>
      ) : (
        <></>
      )}
      <Modal
        title="Ficha actualizada con éxito"
        show={showModal}
        onClose={handleCloseModal}
        onAceptar={handleCloseModal}
        btnAceptar="Volver al inicio"
        hideClose={true}
      >
        <div style={{ textAlign: "center" }}>
          <FontAwesomeIcon color="#20c997" icon={faCheckCircle} fontSize={80} />
          <p className="mt-3">La ficha médica se ha guardado correctamente.</p>
        </div>
      </Modal>
    </form>
  );
}
